import Input from 'client/components/core/Sidebar/components/Settings/components/Input/Input'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { OfferPriceInterface } from 'common/types/entities/OfferPriceInterface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import TextFontUpdater from '../../../components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import FontSize from '../../../components/core/Sidebar/components/Settings/styles/FontSize'
import LineHeight from '../../../components/core/Sidebar/components/Settings/styles/LineHeight'

function OfferPriceSettings({
  entity,
  defaultEntity,
}: {
  entity: OfferPriceInterface
  defaultEntity?: OfferPriceInterface
}) {
  const { isMobile } = useDeviceModeContext()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<OfferPriceInterface>()
  return (
    <>
      <Input
        label="entity_settings.offer_price.custom_price_description"
        value={entity.customDescription}
        update={updateProp('customDescription')}
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'nameFontSize',
                'nameFontFamily',
                'nameFontFileId',
                'nameFontWeight',
                'nameLineHeight',
                'nameColor',
              ]}
              mobileKeys={[
                'mobileNameFontSize',
                'mobileNameFontFamily',
                'mobileNameFontFileId',
                'mobileNameFontWeight',
                'mobileNameLineHeight',
                'mobileNameColor',
              ]}
            />
          )
        }
      >
        entity_settings.price_plan.name_title
      </GroupTitle>
      <FontSize
        update={updateProp('nameFontSize')}
        mobileUpdate={updateProp('mobileNameFontSize')}
        fontSize={entity.nameFontSize}
        mobileFontSize={entity.mobileNameFontSize}
      />
      <LineHeight
        fontSize={entity.nameFontSize}
        mobileFontSize={entity.mobileNameFontSize}
        update={updateProp('nameLineHeight')}
        mobileUpdate={updateProp('mobileNameLineHeight')}
        lineHeight={entity.nameLineHeight}
        mobileLineHeight={entity.mobileNameLineHeight}
      />
      <TextFontUpdater<OfferPriceInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'nameFontFamily'}
        fontWeightKey={'nameFontWeight'}
        fontStyleKey={'nameFontStyle'}
        mobileFontFamilyKey={'mobileNameFontFamily'}
        mobileFontWeightKey={'mobileNameFontWeight'}
        mobileFontStyleKey={'mobileNameFontStyle'}
      />
      <ColorPickerControl
        label="global_settings.text_color.label"
        update={updateProp('nameColor')}
        color={entity.nameColor}
        mobileUpdate={updateProp('mobileNameColor')}
        mobileColor={entity.mobileNameColor}
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'amountFontSize',
                'amountFontFamily',
                'amountFontFileId',
                'amountFontWeight',
                'amountLineHeight',
                'amountColor',
              ]}
              mobileKeys={[
                'mobileAmountFontSize',
                'mobileAmountFontFamily',
                'mobileAmountFontFileId',
                'mobileAmountFontWeight',
                'mobileAmountLineHeight',
                'mobileAmountColor',
              ]}
            />
          )
        }
      >
        entity_settings.price_plan.amount_title
      </GroupTitle>
      <FontSize
        update={updateProp('amountFontSize')}
        mobileUpdate={updateProp('mobileAmountFontSize')}
        fontSize={entity.amountFontSize}
        mobileFontSize={entity.mobileAmountFontSize}
      />
      <LineHeight
        fontSize={entity.amountFontSize}
        mobileFontSize={entity.mobileAmountFontSize}
        update={updateProp('amountLineHeight')}
        mobileUpdate={updateProp('mobileAmountLineHeight')}
        lineHeight={entity.amountLineHeight}
        mobileLineHeight={entity.mobileAmountLineHeight}
      />
      <TextFontUpdater<OfferPriceInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'amountFontFamily'}
        fontWeightKey={'amountFontWeight'}
        fontStyleKey={'amountFontStyle'}
        mobileFontFamilyKey={'mobileAmountFontFamily'}
        mobileFontWeightKey={'mobileAmountFontWeight'}
        mobileFontStyleKey={'mobileAmountFontStyle'}
      />
      <ColorPickerControl
        label="global_settings.text_color.label"
        update={updateProp('amountColor')}
        color={entity.amountColor}
        mobileUpdate={updateProp('mobileAmountColor')}
        mobileColor={entity.mobileAmountColor}
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'descriptionFontSize',
                'descriptionFontFamily',
                'descriptionFontFileId',
                'descriptionFontWeight',
                'descriptionLineHeight',
                'descriptionColor',
              ]}
              mobileKeys={[
                'mobileDescriptionFontSize',
                'mobileDescriptionFontFamily',
                'mobileDescriptionFontFileId',
                'mobileDescriptionFontWeight',
                'mobileDescriptionLineHeight',
                'mobileDescriptionColor',
              ]}
            />
          )
        }
      >
        entity_settings.price_plan.description_title
      </GroupTitle>
      <FontSize
        update={updateProp('descriptionFontSize')}
        mobileUpdate={updateProp('mobileDescriptionFontSize')}
        fontSize={entity.descriptionFontSize}
        mobileFontSize={entity.mobileDescriptionFontSize}
      />
      <LineHeight
        fontSize={entity.descriptionFontSize}
        mobileFontSize={entity.mobileDescriptionFontSize}
        update={updateProp('descriptionLineHeight')}
        mobileUpdate={updateProp('mobileDescriptionLineHeight')}
        lineHeight={entity.descriptionLineHeight}
        mobileLineHeight={entity.mobileDescriptionLineHeight}
      />
      <TextFontUpdater<OfferPriceInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'descriptionFontFamily'}
        fontWeightKey={'descriptionFontWeight'}
        fontStyleKey={'descriptionFontStyle'}
        mobileFontFamilyKey={'mobileDescriptionFontFamily'}
        mobileFontWeightKey={'mobileDescriptionFontWeight'}
        mobileFontStyleKey={'mobileDescriptionFontStyle'}
      />
      <ColorPickerControl
        label="global_settings.text_color.label"
        update={updateProp('descriptionColor')}
        color={entity.descriptionColor}
        mobileUpdate={updateProp('mobileDescriptionColor')}
        mobileColor={entity.mobileDescriptionColor}
      />
      <SizeAndPositionGroup
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default OfferPriceSettings
